import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Divider,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Tooltip,
  Container,
} from "@mui/material";
import {
  AccessTime,
  Place,
  Phone,
  Business,
  Person,
  DeleteOutline,
  CheckCircleOutline,
  DoNotDisturbAlt,
  EventBusy,
  Message,
  Link,
} from "@mui/icons-material";

const IntList = () => {
  const [intData, setIntData] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getInterviews = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        "https://interview-scheduler-80359-default-rtdb.firebaseio.com/.json"
      );

      console.log("response", res.data);
      if (res.data) {
        const formattedData = Object.keys(res.data).map((key) => ({
          id: key, // Adding the key as an "id"
          ...res.data[key], // Spreading the existing object properties
        }));

        console.log("Formatted Data:", formattedData);
        setIntData(formattedData);
      } else {
        setIntData([]);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const deleteInterview = async (id) => {
    try {
      const res = await axios.delete(
        `https://interview-scheduler-80359-default-rtdb.firebaseio.com/${id}.json`
      );
      setIntData(intData.filter((int) => int.id !== id));

      console.log("response", res.data);
    } catch (error) {
      console.log("error", error);
    }
  };

  const updateInterview = async (obj, status) => {
    const { id, ...objWithoutId } = obj;
    try {
      const res = await axios.put(
        `https://interview-scheduler-80359-default-rtdb.firebaseio.com/${id}.json`,
        { ...objWithoutId, status }
      );
      setIntData(
        intData.map((int) => (int.id === id ? { ...int, status } : int))
      );
      console.log("response", res.data);
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    getInterviews();
  }, []);

  // Get current date for comparison
  const currentDate = new Date();

  // Organize interviews into categories
  const categorizedInterviews = useMemo(() => {
    const upcoming = [];
    const completed = [];
    const ignored = [];
    const forgotten = [];
    const past = [];

    intData.forEach((interview) => {
      const interviewDate = new Date(interview.Date);

      // Add to specific status categories
      if (interview.status === "Completed") {
        completed.push(interview);
      } else if (interview.status === "Ignored") {
        ignored.push(interview);
      } else if (interview.status === "Forgot") {
        forgotten.push(interview);
      }
      // Add to upcoming or past based on date
      else if (interviewDate > currentDate) {
        upcoming.push(interview);
      } else if (interviewDate <= currentDate) {
        past.push(interview);
      }
    });

    return { upcoming, completed, ignored, forgotten, past };
  }, [intData, currentDate]);

  // Get interviews for current tab
  const getTabContent = () => {
    switch (tabValue) {
      case 0:
        return categorizedInterviews.upcoming;
      case 1:
        return categorizedInterviews.past;
      case 2:
        return categorizedInterviews.completed;
      case 3:
        return categorizedInterviews.ignored;
      case 4:
        return categorizedInterviews.forgotten;
      default:
        return [];
    }
  };

  // Status chip color mapping
  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "success";
      case "Ignored":
        return "warning";
      case "Forgot":
        return "error";
      default:
        return "primary";
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ mb: 3, fontWeight: "bold" }}
      >
        Interviews List
      </Typography>

      <Paper elevation={3} sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label={`Upcoming (${categorizedInterviews.upcoming.length})`} />
          <Tab label={`Past (${categorizedInterviews.past.length})`} />
          <Tab
            label={`Completed (${categorizedInterviews.completed.length})`}
          />
          <Tab label={`Ignored (${categorizedInterviews.ignored.length})`} />
          <Tab
            label={`Forgotten (${categorizedInterviews.forgotten.length})`}
          />
        </Tabs>
      </Paper>

      {loading ? (
        <Typography variant="body1" sx={{ textAlign: "center", mt: 4, p: 4 }}>
          Loading interviews...
        </Typography>
      ) : getTabContent().length === 0 ? (
        <Typography
          variant="body1"
          sx={{
            textAlign: "center",
            mt: 4,
            p: 4,
            bgcolor: "background.paper",
            borderRadius: 1,
          }}
        >
          No interviews in this category
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {getTabContent().map((int, index) => (
            <Grid
              item
              xs={12}
              md={6}
              key={int.id || index}
              onClick={() => {
                console.log(int);
              }}
            >
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  transition: "transform 0.2s",
                  "&:hover": { transform: "translateY(-4px)" },
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography variant="h6" component="h2">
                      {int.company || "No Company"}
                    </Typography>
                    <Chip
                      label={int.status || "Pending"}
                      color={getStatusColor(int.status)}
                      size="small"
                    />
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box display="flex" gap={1} alignItems="center">
                        <AccessTime fontSize="small" color="action" />
                        <Typography variant="body2">
                          {int.Date || "No date"}{" "}
                          {int.timeSlot ? `- ${int.timeSlot}` : ""}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box display="flex" gap={1} alignItems="center">
                        <Person fontSize="small" color="action" />
                        <Typography variant="body2">
                          {int.YourName || "No name"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box display="flex" gap={1} alignItems="center">
                        <Business fontSize="small" color="action" />
                        <Typography variant="body2">
                          {int.interviewType || "Not specified"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box display="flex" gap={1} alignItems="center">
                        {int.interviewType == "online" ? (
                          <Link fontSize="small" color="action" />
                        ) : (
                          <Place fontSize="small" color="action" />
                        )}

                        <Typography variant="body2" noWrap>
                          {int.interviewType == "online"
                            ? int.meetingLink
                            : int.Location || "No location"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box display="flex" gap={1} alignItems="center">
                        <Phone fontSize="small" color="action" />
                        <Typography variant="body2">
                          {int.phone || "No phone"}
                        </Typography>
                      </Box>
                    </Grid>

                    {int.message && (
                      <Grid item xs={12}>
                        <Box display="flex" gap={1} alignItems="flex-start">
                          <Message
                            fontSize="small"
                            color="action"
                            sx={{ mt: 0.5 }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ wordBreak: "break-word" }}
                          >
                            {int.message}
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {int.createdDate && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Created: {int.createdDate}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    gap={1}
                    flexWrap="wrap"
                  >
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteInterview(int.id)}
                      >
                        <DeleteOutline />
                      </IconButton>
                    </Tooltip>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Tooltip title="Mark as Ignored">
                        <Button
                          variant={
                            int.status === "Ignored" ? "contained" : "outlined"
                          }
                          color="warning"
                          size="small"
                          startIcon={<DoNotDisturbAlt />}
                          onClick={() => updateInterview(int, "Ignored")}
                          sx={{ mb: { xs: 1, md: 0 } }}
                        >
                          Ignore
                        </Button>
                      </Tooltip>
                      <Tooltip title="Mark as Completed">
                        <Button
                          variant={
                            int.status === "Completed"
                              ? "contained"
                              : "outlined"
                          }
                          color="success"
                          size="small"
                          startIcon={<CheckCircleOutline />}
                          onClick={() => updateInterview(int, "Completed")}
                          sx={{ mb: { xs: 1, md: 0 } }}
                        >
                          Complete
                        </Button>
                      </Tooltip>
                      <Tooltip title="Mark as Forgotten">
                        <Button
                          variant={
                            int.status === "Forgot" ? "contained" : "outlined"
                          }
                          color="error"
                          size="small"
                          startIcon={<EventBusy />}
                          onClick={() => updateInterview(int, "Forgot")}
                        >
                          Forgot
                        </Button>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default IntList;
